import {
  BuildingOffice2Icon,
  BuildingStorefrontIcon,
  CubeIcon,
  FolderIcon,
  UserGroupIcon,
  UsersIcon,
  TagIcon,
  CalendarDaysIcon,
  EnvelopeIcon,
  EyeSlashIcon,
  ShoppingBagIcon,
  EnvelopeOpenIcon,
  ChartBarIcon,
  BookOpenIcon,
  DocumentDuplicateIcon,
  RectangleStackIcon,
  MegaphoneIcon,
  CurrencyDollarIcon,
  ClipboardDocumentListIcon,
  MapPinIcon,
  BellIcon,
  ShieldCheckIcon,
  CalculatorIcon,
  ChartPieIcon,
  PhotoIcon,
  TrophyIcon,
} from "@heroicons/react/24/outline";
import { NavigationItem } from "./types";
import { GraduationCap, Percent, TicketIcon } from "lucide-react";

/**
 * Each NavigationItem can have:
 * - name (string)
 * - href (string)
 * - icon (React component)
 * - current (boolean)
 * - internalName (string)
 * - permissions (string[]) - nav-item based permissions
 * - isCertainPathOnly (boolean)
 * - children?: NavigationItem[]
 */
const navigation: NavigationItem[] = [
  {
    name: "Events",
    href: "/admin/events",
    icon: CalendarDaysIcon,
    current: false,
    internalName: "event-registration-admin",
    permissions: ["administration-events"],
    isCertainPathOnly: true,
    children: [
      {
        name: "Management",
        href: "/admin/events",
        icon: ClipboardDocumentListIcon,
        current: false,
        isCertainPathOnly: true,
        permissions: ["administration-events"],
        internalName: "event-management",
      },
      {
        name: "Categories",
        href: "/admin/event-categories",
        icon: FolderIcon,
        current: false,
        isCertainPathOnly: true,
        permissions: ["administration-events"],
        internalName: "event_categories",
      },
      {
        name: "Instructors",
        href: "/admin/event-instructors",
        icon: GraduationCap,
        current: false,
        isCertainPathOnly: true,
        permissions: ["administration-events"],
        internalName: "event_instructors",
      },
      {
        name: "Venues",
        href: "/admin/venues",
        icon: MapPinIcon,
        current: false,
        isCertainPathOnly: true,
        permissions: ["administration-events"],
        internalName: "event_venues",
      },
      {
        name: "Vouchers",
        href: "/admin/vouchers",
        icon: TicketIcon,
        current: false,
        isCertainPathOnly: true,
        permissions: ["administration-events"],
        internalName: "event_vouchers",
      },
      {
        name: "Discounts",
        href: "/admin/discounts",
        icon: Percent,
        current: false,
        isCertainPathOnly: true,
        permissions: ["administration-events"],
        internalName: "event_discounts",
      },
      {
        name: "Notifications",
        href: "/admin/event-notifications",
        icon: BellIcon,
        current: false,
        isCertainPathOnly: true,
        permissions: ["administration-event-notifications"],
        internalName: "event_notifications",
      },
      {
        name: "Reports",
        href: "#",
        icon: ChartBarIcon,
        current: false,
        isCertainPathOnly: true,
        permissions: ["administration-events"],
        internalName: "event_reports",
        children: [
          // Ordered by importance and grouped by purpose:
          // attendee/session operations first, then voucher tracking,
          // then the finance-owned DRAFT reports.
          {
            name: "Registrations",
            href: "/admin/events/reports/registrations",
            icon: ClipboardDocumentListIcon,
            current: false,
            isCertainPathOnly: true,
            permissions: ["administration-events"],
            internalName: "event_reports_registrations",
          },
          {
            name: "Roster Management",
            href: "/admin/events/reports/roster-management",
            icon: ClipboardDocumentListIcon,
            current: false,
            isCertainPathOnly: true,
            permissions: ["administration-events"],
            internalName: "event_reports_roster_management",
          },
          {
            name: "Sessions",
            href: "/admin/events/reports/sessions",
            icon: ClipboardDocumentListIcon,
            current: false,
            isCertainPathOnly: true,
            permissions: ["administration-events"],
            internalName: "event_reports_sessions",
          },
          {
            name: "Training History",
            href: "/admin/events/reports/training-history",
            icon: ClipboardDocumentListIcon,
            current: false,
            isCertainPathOnly: true,
            permissions: ["administration-events"],
            internalName: "event_reports_training_history",
          },
          {
            name: "Vouchers",
            href: "/admin/events/reports/vouchers",
            icon: ClipboardDocumentListIcon,
            current: false,
            isCertainPathOnly: true,
            permissions: ["administration-events"],
            internalName: "event_reports_vouchers",
          },
          {
            name: "Products History",
            href: "/admin/events/reports/products-history",
            icon: ClipboardDocumentListIcon,
            current: false,
            isCertainPathOnly: true,
            permissions: ["administration-events"],
            internalName: "event_reports_products_history",
          },
          {
            name: "Transaction History",
            href: "/admin/events/reports/transaction-history",
            icon: ClipboardDocumentListIcon,
            current: false,
            isCertainPathOnly: true,
            permissions: ["administration-events"],
            internalName: "event_reports_transaction_history",
          },
        ],
      },
    ],
  },

  {
    name: "Email Management",
    href: "/admin/email-templates",
    icon: EnvelopeIcon,
    current: false,
    internalName: "email_management",
    permissions: ["administration-email-management"],
    isCertainPathOnly: true,
    children: [
      {
        name: "Email Templates",
        href: "/admin/email-templates",
        icon: DocumentDuplicateIcon,
        current: false,
        internalName: "email_templates",
        isCertainPathOnly: true,
        permissions: ["administration-email-management"],
      },
      {
        name: "Email Campaigns",
        href: "/admin/email-campaigns",
        icon: MegaphoneIcon,
        current: false,
        internalName: "email_campaigns",
        isCertainPathOnly: true,
        permissions: ["administration-email-management"],
      },
      {
        name: "Template Categories",
        href: "/admin/email-template-categories",
        icon: FolderIcon,
        current: false,
        internalName: "email_template_categories",
        isCertainPathOnly: true,
        permissions: ["administration-email-management"],
      },
      {
        name: "Email Event Logs",
        href: "/admin/email/activity",
        icon: ChartBarIcon,
        current: false,
        internalName: "email_event_logs",
        isCertainPathOnly: true,
        permissions: ["administration-email-management"],
      },
    ],
  },

  {
    name: "Resource Library",
    href: "#",
    icon: BookOpenIcon,
    current: false,
    internalName: "resource_management",
    permissions: [
      "administration-resource-library",
      "administration-process-wheel",
    ],
    isCertainPathOnly: true,
    children: [
      {
        name: "Resource Management",
        href: "/admin/resources",
        icon: RectangleStackIcon,
        current: false,
        internalName: "resource_management",
        permissions: ["administration-resource-library"],
        isCertainPathOnly: true,
      },
      {
        name: "Resource Categories",
        href: "/admin/resource-categories",
        icon: FolderIcon,
        current: false,
        internalName: "resourceCategories",
        permissions: ["administration-resource-library"],
        isCertainPathOnly: true,
      },
      {
        name: "Resource Tags",
        href: "/admin/resource-tags",
        icon: TagIcon,
        current: false,
        internalName: "resourceTags",
        permissions: ["administration-resource-library"],
        isCertainPathOnly: true,
      },
      {
        name: "Process Wheel",
        href: "/admin/process-wheels",
        icon: ClipboardDocumentListIcon,
        current: false,
        internalName: "processWheel",
        permissions: ["administration-process-wheel"],
        isCertainPathOnly: true,
      },
      {
        name: "Image Remediation",
        href: "/admin/image-remediation",
        icon: PhotoIcon,
        current: false,
        internalName: "image_remediation",
        permissions: ["administration-resource-library"],
        isCertainPathOnly: true,
      },
    ],
  },

  {
    name: "Learning Center",
    href: "#",
    icon: GraduationCap,
    current: false,
    internalName: "learning_center",
    permissions: ["administration-learning-center"],
    isCertainPathOnly: true,
    children: [
      {
        name: "Course Management",
        href: "/admin/learning-center/courses",
        icon: RectangleStackIcon,
        current: false,
        internalName: "course_management",
        permissions: ["administration-learning-center"],
        isCertainPathOnly: true,
      },
      {
        name: "Module Library",
        href: "/admin/learning-center/module-library",
        icon: RectangleStackIcon,
        current: false,
        internalName: "module_library",
        permissions: ["administration-learning-center"],
        isCertainPathOnly: true,
      },
    ],
  },

  {
    name: "Company & Users",
    href: "#",
    icon: BuildingOffice2Icon,
    current: false,
    internalName: "user_management",
    permissions: [
      "administration-companies",
      "administration-users",
      "administration-stages",
    ],
    isCertainPathOnly: true,
    children: [
      {
        name: "Company Management",
        href: "/admin/companies",
        icon: BuildingOffice2Icon,
        current: false,
        internalName: "company_management",
        permissions: ["administration-companies"],
        isCertainPathOnly: true,
      },
      {
        name: "User Management",
        href: "/admin/users",
        icon: UsersIcon,
        current: false,
        internalName: "admin_user_list",
        permissions: ["administration-users"],
        isCertainPathOnly: true,
      },
      {
        name: "Business Roles",
        href: "/admin/business-roles",
        icon: ShieldCheckIcon,
        current: false,
        internalName: "business_roles",
        permissions: ["administration-users"],
        isCertainPathOnly: true,
      },
      {
        name: "Stages",
        href: "/admin/stages",
        icon: RectangleStackIcon,
        current: false,
        internalName: "stages",
        permissions: ["administration-stages"],
        isCertainPathOnly: true,
      },
    ],
  },

  {
    name: "Analytics",
    href: "/admin/data-connector-adoption",
    icon: ChartBarIcon,
    current: false,
    internalName: "analytics",
    permissions: ["administration-data-connector-adoption"],
    isCertainPathOnly: true,
    children: [
      {
        name: "Data Connector Adoption",
        href: "/admin/data-connector-adoption",
        icon: ChartPieIcon,
        current: false,
        internalName: "data_connector_adoption",
        permissions: ["administration-data-connector-adoption"],
        isCertainPathOnly: true,
      },
    ],
  },

  {
    name: "Sales Portal",
    href: "/admin/sales-portal/opportunities",
    icon: CurrencyDollarIcon,
    current: false,
    internalName: "sales_portal",
    permissions: ["sales-portal"],
    isCertainPathOnly: true,
    children: [
      {
        name: "Opportunities",
        href: "/admin/sales-portal/opportunities",
        icon: ClipboardDocumentListIcon,
        current: false,
        internalName: "sales_portal_opportunities",
        permissions: ["sales-portal"],
        isCertainPathOnly: true,
      },
    ],
  },

  {
    name: "Scoreboard",
    href: "/admin/scoreboard/monthly-report",
    icon: TrophyIcon,
    current: false,
    internalName: "scoreboard",
    // NOTE: `admin-monthly-scoreboard` is INTENTIONAL — not a typo of the
    // `administration-*` convention used by sibling admin permissions. It must
    // stay byte-identical with the backend Voter and permission migration; do
    // not rename it here (or on the children below).
    permissions: ["admin-monthly-scoreboard"],
    isCertainPathOnly: true,
    children: [
      {
        name: "Monthly Report",
        href: "/admin/scoreboard/monthly-report",
        icon: ChartBarIcon,
        current: false,
        internalName: "scoreboard_monthly_report",
        permissions: ["admin-monthly-scoreboard"],
        isCertainPathOnly: true,
      },
      {
        name: "Leaderboards",
        href: "/admin/scoreboard/leaderboards",
        icon: TrophyIcon,
        current: false,
        internalName: "scoreboard_leaderboards",
        permissions: ["admin-monthly-scoreboard"],
        isCertainPathOnly: true,
      },
    ],
  },

  {
    name: "Lead Generation",
    href: "#",
    icon: ChartBarIcon,
    current: false,
    internalName: "admin",
    permissions: ["administration-stochastic"],
    isCertainPathOnly: true,
    children: [
      {
        name: "Campaign Status",
        href: "/lead-generation/campaign-status",
        icon: ChartBarIcon,
        current: false,
        internalName: "stochastic_campaign_status",
        permissions: ["administration-stochastic"],
      },
      {
        name: "Campaign Job Status",
        href: "/lead-generation/campaign-job-status",
        icon: MegaphoneIcon,
        current: false,
        internalName: "stochastic_mailing",
        permissions: ["administration-stochastic"],
      },
      {
        name: "Campaign Billing",
        href: "#",
        icon: CurrencyDollarIcon,
        current: false,
        internalName: "campaign_billing",
        permissions: ["administration-stochastic"],
        children: [
          {
            name: "Metered Billing",
            href: "/lead-generation/campaigns/metered-billing",
            icon: CalculatorIcon,
            current: false,
            internalName: "campaign_billing_metered",
            permissions: ["administration-stochastic"],
          },
          {
            name: "Subscription Billing",
            href: "/lead-generation/campaigns/subscription-billing",
            icon: CalendarDaysIcon,
            current: false,
            internalName: "campaign_billing_subscription",
            permissions: ["administration-stochastic"],
          },
        ],
      },
      {
        name: "Postage Expense",
        href: "/lead-generation/campaigns/postage",
        icon: EnvelopeOpenIcon,
        current: false,
        internalName: "campaignPostage",
        permissions: ["administration-stochastic"],
      },
      {
        name: "Do Not Mail",
        href: "/lead-generation/do-not-mail",
        icon: EyeSlashIcon,
        current: false,
        internalName: "doNotMail",
        permissions: ["administration-stochastic"],
      },
      {
        name: "Manage Products",
        href: "/lead-generation/products/campaign",
        icon: ShoppingBagIcon,
        current: false,
        internalName: "campaignProducts",
        permissions: ["administration-stochastic"],
      },
      {
        name: "Draft Invoice Calculator",
        href: "/lead-generation/draft-invoice",
        icon: CalculatorIcon,
        current: false,
        internalName: "draft_invoice_calculator",
        permissions: ["administration-stochastic"],
      },
    ],
  },

  {
    name: "Product Store",
    href: "#",
    icon: BuildingStorefrontIcon,
    current: false,
    internalName: "product_store",
    permissions: ["administration-product-store"],
    isCertainPathOnly: true,
    children: [
      {
        name: "Products",
        href: "/lead-generation/product-store/products",
        icon: CubeIcon,
        current: false,
        internalName: "product_store_products",
        permissions: ["administration-product-store"],
      },
      {
        name: "Categories",
        href: "/lead-generation/product-store/categories",
        icon: FolderIcon,
        current: false,
        internalName: "product_store_categories",
        permissions: ["administration-product-store"],
      },
      {
        name: "Orders",
        href: "/lead-generation/product-store/orders",
        icon: ShoppingBagIcon,
        current: false,
        internalName: "product_store_orders",
        permissions: ["administration-product-store"],
      },
    ],
  },

  {
    name: "Employee Directory",
    href: "/admin/employee-directory/profiles",
    icon: UserGroupIcon,
    current: false,
    internalName: "employee_directory",
    permissions: ["employee-directory-management"],
    isCertainPathOnly: true,
    children: [
      {
        name: "Employee Profiles",
        href: "/admin/employee-directory/profiles",
        icon: UserGroupIcon,
        current: false,
        internalName: "employee_directory_profiles",
        isCertainPathOnly: true,
        permissions: ["employee-directory-management"],
      },
      {
        name: "Specialties",
        href: "/admin/employee-directory/specialties",
        icon: FolderIcon,
        current: false,
        internalName: "employee_directory_specialties",
        isCertainPathOnly: true,
        permissions: ["employee-directory-management"],
      },
      {
        name: "Teams",
        href: "/admin/employee-directory/teams",
        icon: FolderIcon,
        current: false,
        internalName: "employee_directory_teams",
        isCertainPathOnly: true,
        permissions: ["employee-directory-management"],
      },
    ],
  },

  {
    name: "File Manager",
    href: "#file-manager",
    icon: FolderIcon,
    current: false,
    internalName: "file_manager",
    permissions: ["file-manager"],
    isDialog: true,
    dialogAction: "fileManager",
  },
];

const adminConfig = {
  sectionName: "admin",
  defaultRoute: "/admin/companies",
  navigation,
};

export default adminConfig;
